// ProfessoresView — lista, busca, adicionar e remover professores da company.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  GraduationCap,
  Loader2,
  Plus,
  Search,
  Trash2,
  X
} from "lucide-react";
import { api, ApiError, type TeacherRow } from "../api";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const teachers = signal<TeacherRow[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const adding = signal<boolean>(false);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listProfessors();
    teachers.value = r.teachers;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function ProfessoresView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading")
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  if (phase.value === "error")
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" /> {errMsg.value}
      </div>
    );
  const rows = teachers.value ?? [];
  const q = search.value.toLowerCase().trim();
  const visible = useMemo(() => {
    return rows.filter((t) => {
      if (q) {
        const hay = `${t.email} ${t.name} ${t.apelido ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q]);

  async function remove(t: TeacherRow) {
    if (!confirm(`Remover ${t.name} como professor da organização?`)) return;
    try {
      await api.removeProfessor(t.userId);
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  return (
    <div class="space-y-4">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <GraduationCap className="size-6 text-primary" />
            Professores
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            {rows.length} professor{rows.length !== 1 ? "es" : ""} vinculado{rows.length !== 1 ? "s" : ""}.
          </p>
        </div>
        <button
          type="button"
          onClick={() => (adding.value = true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" /> Adicionar professor
        </button>
      </header>

      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar email, nome…"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum professor encontrado.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Professor</th>
                <th class="px-3 py-2 text-right">Disciplinas</th>
                <th class="px-3 py-2 text-right">Bancos</th>
                <th class="px-3 py-2 text-right">Turmas</th>
                <th class="px-3 py-2">Status</th>
                <th class="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => (
                <tr key={t.userId} class="border-b border-border/60 last:border-0 hover:bg-accent/30">
                  <td class="px-3 py-2">
                    <div class="flex items-center gap-2 font-semibold">
                      <GraduationCap width="0.9em" height="0.9em" className="text-violet-600 dark:text-violet-300" />
                      {t.name}
                    </div>
                    <div class="font-mono text-[11px] text-muted-foreground">{t.email}</div>
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">{t.disciplinaCount}</td>
                  <td class="px-3 py-2 text-right tabular-nums">{t.bankCount}</td>
                  <td class="px-3 py-2 text-right tabular-nums">{t.groupCount}</td>
                  <td class="px-3 py-2">
                    {t.suspended ? (
                      <span class="inline-flex items-center gap-1 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-600">
                        <Ban width="0.85em" height="0.85em" /> Suspenso
                      </span>
                    ) : (
                      <span class="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-300">
                        <CheckCircle2 width="0.85em" height="0.85em" /> Ativo
                      </span>
                    )}
                  </td>
                  <td class="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => remove(t)}
                      title="Remover vínculo"
                      aria-label="Remover"
                      class="rounded-md border border-red-500/40 bg-red-500/5 px-2 py-1 text-xs text-red-600 hover:bg-red-500/10 dark:text-red-400"
                    >
                      <Trash2 width="0.9em" height="0.9em" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding.value && <AddModal />}
    </div>
  );
}

function AddModal() {
  const [userIdInput, setUserIdInput] = useState("");
  const [busy, setBusy] = useState(false);
  async function go() {
    if (!userIdInput.trim()) return;
    setBusy(true);
    try {
      await api.addProfessor(userIdInput.trim());
      adding.value = false;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={() => (adding.value = false)}
    >
      <div
        class="w-full max-w-md space-y-3 rounded-md border border-border bg-card p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="flex items-center justify-between">
          <h2 class="text-base font-bold">Adicionar professor</h2>
          <button
            type="button"
            onClick={() => (adding.value = false)}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </div>
        <p class="text-xs text-muted-foreground">
          Digite o <strong>email</strong> do professor já cadastrado na plataforma.
          Se ainda não existe, crie o usuário no Manager primeiro e tente de novo.
        </p>
        <input
          type="email"
          value={userIdInput}
          onInput={(e) => setUserIdInput((e.target as HTMLInputElement).value)}
          autoFocus
          aria-label="Email do professor"
          placeholder="professor@exemplo.com"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
        <div class="flex items-center justify-end gap-2 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => (adding.value = false)}
            disabled={busy}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={go}
            disabled={busy || !userIdInput.trim()}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus width="1em" height="1em" />}
            Adicionar
          </button>
        </div>
      </div>
    </div>
  );
}
